import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useAppServices } from "../../../../hook/services";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import { useAgencyInfo } from "../../../../context/agency";
import ThemedLoader from "../../../../components/ThemedLoader";

const Configuration = () => {
  const AppService = useAppServices();
  const [agency, Update] = useAgencyInfo();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const [loading, setLoading] = useState(true);
  const [formValues, setFormValues] = useState(
    agency?.support_configuration || {}
  );
  const [locations, setLocations] = useState([]);

  const getLocations = async () => {
    try {
      const { response } =
        await AppService.sidenav_customizer_custom_level.getLocations({
          query: `company_id=${companyId}`,
        });
      if (response && Array.isArray(response.accounts)) {
        const mappedLocations = response.accounts.map((loc) => ({
          value: loc.id,
          label: loc.name,
        }));
        setLocations(mappedLocations);
      } else {
        setLocations([]);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getLocations();
  }, []);

  const handleChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const configureCustomMenuLink = async (e) => {
    e.preventDefault();
    try {
      const { response } = await AppService.support.create_custom_menu_link({
        payload: {
          ...formValues,
          companyId: companyId,
          customMenuLinkId: formValues?.customMenuLinkId || null,
        },
      });

      if (response) {
        toast.success("Configuration saved successfully.");
        Update(response.data);
      } else {
        toast.error("An error occurred. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    }
  };

  if (loading) {
    return <ThemedLoader variant="page" label="Loading configuration" />;
  }

  return (
    <div className="cfg-page">
      <div className="container-fluid py-4 px-4">
        {/* Back */}
        <button
          className="cfg-back-btn d-inline-flex align-items-center mb-4 bg-transparent border-0 p-0 text-secondary"
          type="button"
          onClick={() => navigate(-1)}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>Back</span>
        </button>

        {/* Header */}
        <header className="mb-4 d-flex align-items-center gap-3">
          <span className="cfg-header-icon d-inline-flex align-items-center justify-content-center">
            <Icon icon="mdi:cog" width="18" />
          </span>
          <div>
            <h3 className="cfg-title mb-1" style={{ fontSize: "1.375rem", fontWeight: 700, letterSpacing: "-0.015em", lineHeight: 1.15, color: "#0f172a" }}>
              Support configuration
            </h3>
            <p className="cfg-subtitle mb-0">
              Configure how the support feature appears in your sub-account locations.
            </p>
          </div>
        </header>

        {/* Custom Menu Link */}
        <section className="cfg-card mb-3">
          <div className="cfg-card-header">
            <div className="cfg-card-eyebrow">Custom menu link</div>
            <h5 className="cfg-card-title mb-0">Configure custom page for locations</h5>
            <p className="cfg-card-meta mb-0">
              Add a support menu link inside selected sub-account sidebars so they can open the support page directly.
            </p>
          </div>

          <div className="cfg-card-body">
            <div className="cfg-field">
              <label className="cfg-label">Custom menu link title</label>
              <input
                type="text"
                className="cfg-input"
                placeholder="e.g. Help & Support"
                value={formValues.customMenuLinkTitle}
                onChange={(e) => handleChange("customMenuLinkTitle", e.target.value)}
              />
              <small className="cfg-helper">This is the label users will see in the sidebar.</small>
            </div>

            <div className="cfg-field mb-0">
              <label className="cfg-label">
                <span>Locations</span>
                {Array.isArray(formValues.customMenuLinkLocations) &&
                  formValues.customMenuLinkLocations.length > 0 && (
                    <span className="cfg-count-pill">
                      {formValues.customMenuLinkLocations.length} selected
                    </span>
                  )}
              </label>
              <Select
                options={locations}
                isMulti
                value={formValues.customMenuLinkLocations}
                onChange={(e) =>
                  setFormValues((prev) => ({
                    ...prev,
                    customMenuLinkLocations: e,
                  }))
                }
                placeholder="Select locations to add the link to"
                classNamePrefix="cfg-rs"
                styles={selectStyles}
              />
              <small className="cfg-helper">Pick one or more sub-account locations to receive this menu link.</small>
            </div>
          </div>

          <div className="cfg-card-footer d-flex justify-content-end">
            <button
              type="button"
              className="cfg-btn cfg-btn--primary"
              onClick={configureCustomMenuLink}
            >
              <Icon icon="mdi:link" width="14" className="me-1" />
              {formValues?.customMenuLinkId ? "Update" : "Create"} custom menu link
            </button>
          </div>
        </section>
      </div>

      <style>{`
        .cfg-page {
          font-family: 'Inter', sans-serif;
          background: #fafafa;
          min-height: 100vh;
        }

        .cfg-back-btn { transition: color 0.15s ease; }
        .cfg-back-btn:hover { color: #1f2937 !important; }
        .cfg-back-btn:hover svg { transform: translateX(-2px); }
        .cfg-back-btn svg { transition: transform 0.15s ease; }

        .cfg-header-icon {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
          color: #4f46e5;
          flex-shrink: 0;
          border: 1px solid #e0e7ff;
          box-shadow: 0 1px 2px rgba(79, 70, 229, 0.06);
        }
        .cfg-subtitle {
          font-size: 0.8125rem;
          color: #6b7280;
          line-height: 1.5;
        }

        /* Card */
        .cfg-card {
          background: #fff;
          border: 1px solid #ececec;
          border-radius: 14px;
          overflow: hidden;
        }
        .cfg-card-header {
          padding: 16px 20px 14px;
          border-bottom: 1px solid #f1f1f1;
          background: #fafafa;
        }
        .cfg-card-eyebrow {
          font-size: 0.6875rem;
          font-weight: 600;
          color: #4f46e5;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin-bottom: 4px;
        }
        .cfg-card-title {
          font-size: 0.9375rem;
          font-weight: 600;
          color: #0f172a;
          letter-spacing: -0.005em;
          line-height: 1.3;
        }
        .cfg-card-meta {
          font-size: 0.8125rem;
          color: #6b7280;
          line-height: 1.5;
          margin-top: 4px !important;
        }
        .cfg-card-body { padding: 18px 20px; }
        .cfg-card-footer {
          padding: 14px 20px;
          border-top: 1px solid #f1f1f1;
          background: #fff;
        }

        /* Fields */
        .cfg-field { margin-bottom: 18px; }
        .cfg-label {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.8125rem;
          font-weight: 500;
          color: #1f2937;
          margin-bottom: 6px;
        }
        .cfg-count-pill {
          display: inline-flex;
          align-items: center;
          font-size: 0.6875rem;
          font-weight: 500;
          color: #4f46e5;
          background: #eef2ff;
          border: 1px solid #e0e7ff;
          padding: 2px 8px;
          border-radius: 999px;
        }
        .cfg-helper {
          display: block;
          font-size: 0.75rem;
          color: #9ca3af;
          margin-top: 6px;
          line-height: 1.5;
        }
        .cfg-input {
          width: 100%;
          height: 40px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 0 12px;
          background: #fff;
          font-size: 0.875rem;
          color: #1f2937;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
          font-family: inherit;
        }
        .cfg-input::placeholder { color: #9ca3af; }
        .cfg-input:focus {
          outline: none;
          border-color: #4f46e5;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
        }

        /* Buttons */
        .cfg-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8125rem;
          font-weight: 600;
          letter-spacing: 0.005em;
          padding: 9px 16px;
          border-radius: 9px;
          border: 1px solid transparent;
          line-height: 1;
          cursor: pointer;
          font-family: inherit;
          transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease;
        }
        .cfg-btn:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
        }
        .cfg-btn--primary {
          background: linear-gradient(180deg, #6366f1 0%, #4f46e5 100%);
          color: #fff;
          border-color: rgba(255, 255, 255, 0.04);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 1px 2px rgba(79, 70, 229, 0.22),
            0 4px 10px rgba(79, 70, 229, 0.2);
        }
        .cfg-btn--primary:hover {
          background: linear-gradient(180deg, #4f46e5 0%, #4338ca 100%);
          transform: translateY(-1px);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 2px 4px rgba(67, 56, 202, 0.28),
            0 8px 18px rgba(79, 70, 229, 0.3);
        }
        .cfg-btn--primary:active { transform: translateY(0); }
      `}</style>
    </div>
  );
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderColor: state.isFocused ? "#4f46e5" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(79, 70, 229, 0.1)" : "none",
    borderRadius: 8,
    "&:hover": { borderColor: state.isFocused ? "#4f46e5" : "#d1d5db" },
    fontSize: "0.875rem",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#4f46e5"
      : state.isFocused
      ? "#f5f3ff"
      : "#fff",
    color: state.isSelected ? "#fff" : "#1f2937",
    fontSize: "0.875rem",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 10,
    boxShadow: "0 8px 24px rgba(17, 24, 39, 0.08)",
    border: "1px solid #ececec",
  }),
  multiValue: (base) => ({
    ...base,
    background: "#eef2ff",
    border: "1px solid #e0e7ff",
    borderRadius: 6,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#4338ca",
    fontWeight: 500,
    fontSize: "0.75rem",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#4f46e5",
    ":hover": { background: "#4f46e5", color: "#fff" },
  }),
};

export default Configuration;
